// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { CircleHelp, Filter, RefreshCw, Search, X } from "lucide-react";
import { useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import type { StoreScorecardPanelName, StoreSettingsDiffRow } from "@/api/hooks/use-store-scorecard";
import {
  STORE_SCORECARD_PANELS,
  useStoreScorecardPanelQuery,
  useStoreSettingsDiffQuery,
} from "@/api/hooks/use-store-scorecard";
import { DateValue } from "@/components/shared/datetime-value";
import { FilterToggle } from "@/components/shared/filter-toggle";
import { KeyChip } from "@/components/shared/key-chip";
import { PanelColumns, PanelError, PanelHeader } from "@/components/shared/panel";
import { ScorecardStatusBadge } from "@/components/shared/scorecard-status-badge";
import { StoreLink } from "@/components/shared/store-link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { SettingValueCell } from "@/features/settings/setting-value-cell";
import { cn } from "@/lib/utils";

interface StoreScorecardTabProps {
  storeName: string;
}

/**
 * Checks whose value is a raw date rather than an already-formatted string. The recency checks
 * emit durations ("4h", "12m") server-side, so `install_date` and `go_live_date` are the only ones
 * that have to go through the app's date formatting instead of being printed as the API sent it.
 */
const DATE_VALUED_CHECKS = new Set(["install_date", "go_live_date"]);

/** A check's value, formatted per the viewer's date preference when it is a date. */
function CheckValue({ checkKey, value }: { checkKey: string; value: string }) {
  // Unset dates arrive as a sentinel word ("(unset)"), so only format what actually parses.
  if (DATE_VALUED_CHECKS.has(checkKey) && value && !Number.isNaN(Date.parse(value))) {
    return <DateValue value={value} />;
  }
  return <>{value}</>;
}

/**
 * The threshold a check was graded against.
 *
 * A whole column of thresholds crowded out the values while being the thing you
 * read least — it only matters once a check is already red. Parked behind a hint
 * icon so the row leads with what was measured.
 */
function ThresholdHint({ threshold }: { threshold: string }) {
  const { t } = useTranslation();
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* A button, not a span: the threshold has to be reachable by keyboard,
            and only a focusable control gets the tooltip on focus. */}
        <button
          type="button"
          className="inline-flex cursor-help text-muted-foreground transition-colors hover:text-foreground"
          aria-label={t("storeScorecard.columns.threshold")}
        >
          <CircleHelp className="h-3.5 w-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="left">
        <span className="text-muted-foreground">{t("storeScorecard.columns.threshold")}: </span>
        {threshold}
      </TooltipContent>
    </Tooltip>
  );
}

/** One scorecard. Loads, errors and refetches entirely on its own. */
function ScorecardPanelSection({
  storeName,
  panel,
  className,
}: {
  storeName: string;
  panel: StoreScorecardPanelName;
  className?: string;
}) {
  const { t } = useTranslation();
  const { data, isLoading, isError, refetch, isFetching } = useStoreScorecardPanelQuery(storeName, panel);

  return (
    <section className={cn("space-y-3", className)}>
      <PanelHeader
        title={t(`storeScorecard.panels.${panel}.title`)}
        description={t(`storeScorecard.panels.${panel}.description`)}
        onRefresh={() => refetch()}
        isRefreshing={isFetching}
      />

      {isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-4/5" />
        </div>
      )}

      {isError && <PanelError onRetry={() => refetch()} isRetrying={isFetching} />}

      {data && (
        <Table>
          <TableHeader>
            {/* Same header fill as the data-table pages and the settings diff below,
                so a scorecard reads as one of the app's tables rather than a list
                that happens to have headings. */}
            <TableRow className="bg-sidebar hover:bg-sidebar">
              <TableHead className="w-[40%]">{t("storeScorecard.columns.check")}</TableHead>
              {/* `w-px` is the shrink-to-fit idiom for an auto-layout table: the browser
                  takes it as "as narrow as the content allows", which for a badge column
                  is the badge. A fixed width here was reserving space for nothing. */}
              <TableHead className="w-px">{t("storeScorecard.columns.status")}</TableHead>
              <TableHead>{t("storeScorecard.columns.value")}</TableHead>
              {/* `border-l-0` because this column has no heading: the divider every
                  other head draws would read as a stray rule off the Value column. */}
              <TableHead className="w-10 border-l-0" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.checks.map((check) => (
              <TableRow key={check.key}>
                <TableCell className="text-sm">{check.key}</TableCell>
                <TableCell className="whitespace-nowrap">
                  <ScorecardStatusBadge status={check.status} />
                </TableCell>
                {/* `whitespace-pre-line`: a check whose value is several independent
                    facts — two clock windows, two feature switches, one line per report
                    definition — sends them newline-separated rather than run together. */}
                <TableCell className="whitespace-pre-line text-sm">
                  <CheckValue checkKey={check.key} value={check.value} />
                </TableCell>
                <TableCell className="text-right">
                  <ThresholdHint threshold={check.threshold} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </section>
  );
}

const ORIGIN_STYLES: Record<string, string> = {
  canon:
    "bg-violet-100 text-violet-700 border-violet-200 dark:bg-violet-950 dark:text-violet-300 dark:border-violet-900",
  reference: "bg-zinc-100 text-zinc-600 border-zinc-200 dark:bg-zinc-900 dark:text-zinc-400 dark:border-zinc-800",
  "store-only":
    "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-900",
};

function OriginBadge({ origin }: { origin: StoreSettingsDiffRow["origin"] }) {
  const { t } = useTranslation();
  return (
    <Badge variant="outline" className={cn("font-normal text-xs", ORIGIN_STYLES[origin])}>
      {t(`storeScorecard.origin.${origin}`)}
    </Badge>
  );
}

function SettingsDiffSection({ storeName }: { storeName: string }) {
  const { t } = useTranslation();
  const { data, isLoading, isError, refetch, isFetching } = useStoreSettingsDiffQuery(storeName);
  const [search, setSearch] = useState("");
  const [onlyDifferences, setOnlyDifferences] = useState(true);

  const rows = useMemo(() => {
    const all = data?.rows ?? [];
    const needle = search.trim().toLowerCase();
    return all.filter((row) => {
      if (onlyDifferences && !row.isDifferent) return false;
      if (!needle) return true;
      return (
        row.setting.toLowerCase().includes(needle) ||
        row.table.toLowerCase().includes(needle) ||
        (row.expected ?? "").toLowerCase().includes(needle) ||
        (row.storeValue ?? "").toLowerCase().includes(needle)
      );
    });
  }, [data?.rows, search, onlyDifferences]);

  const differenceCount = useMemo(() => (data?.rows ?? []).filter((row) => row.isDifferent).length, [data?.rows]);

  return (
    <section className="space-y-3">
      <PanelHeader
        title={t("storeScorecard.panels.settingsDiff.title")}
        description={
          data?.isConfigured && data.referenceStoreName ? (
            // Trans rather than interpolation so the reference store is a real
            // link — one click to go inspect what these rows were graded against.
            <Trans
              i18nKey="storeScorecard.panels.settingsDiff.description"
              ns="back-office"
              components={{
                store: (
                  <StoreLink
                    storeName={data.referenceStoreName}
                    legibleName={data.referenceStoreLegibleName ?? undefined}
                    // Sitting in running text, not a table cell. `inline-flex` is
                    // wrong here: its synthesised baseline comes from the icon (no
                    // text baseline of its own), so the label rides off the line and
                    // `vertical-align` on the box only ever over- or under-shoots.
                    // Dropped to `inline` so the LABEL keeps the sentence's baseline,
                    // with the icon nudged the canonical -0.125em instead — and the
                    // name can now wrap mid-sentence. `gap` does not apply to inline
                    // boxes, hence the explicit margin.
                    className="ml-1 inline [&>svg]:mr-1.5 [&>svg]:inline-block [&>svg]:align-[-0.125em]"
                  />
                ),
              }}
            />
          ) : (
            t("storeScorecard.panels.settingsDiff.descriptionGeneric")
          )
        }
        onRefresh={() => refetch()}
        isRefreshing={isFetching}
      />

      {isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-9 w-56" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-4/5" />
        </div>
      )}

      {isError && <PanelError onRetry={() => refetch()} isRetrying={isFetching} />}

      {data && !data.isConfigured && (
        <p className="rounded-md border bg-muted/40 px-3 py-2 text-muted-foreground text-sm">
          {t("storeScorecard.noReferenceStore")}
        </p>
      )}

      {data?.isConfigured && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
              <div className="w-56">
                <div className="relative">
                  <Search className="absolute top-2.5 left-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder={t("storeScorecard.searchPlaceholder")}
                    className={`w-full pl-9 ${search ? "pr-8" : ""}`}
                  />
                  {search && (
                    <button
                      type="button"
                      onClick={() => setSearch("")}
                      aria-label={t("storeScorecard.clearSearch")}
                      className="absolute top-2.5 right-2.5 text-muted-foreground transition-colors hover:text-foreground"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
              <FilterToggle
                active={onlyDifferences}
                onChange={setOnlyDifferences}
                icon={Filter}
                label={t("storeScorecard.onlyDifferences", { count: differenceCount })}
              />
            </div>
          </div>

          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow className="bg-sidebar hover:bg-sidebar">
                  {/* pl-4 mirrors the leading ID column on the data-table pages
                      (its `meta.className`), so the first column lines up with
                      every other table in the app. */}
                  <TableHead className="w-[14rem] pl-4">{t("storeScorecard.columns.status")}</TableHead>
                  <TableHead>{t("storeScorecard.columns.setting")}</TableHead>
                  <TableHead>{t("storeScorecard.columns.expected")}</TableHead>
                  <TableHead>{t("storeScorecard.columns.storeValue")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={`${row.table}:${row.setting}`} className="group h-12 hover:bg-muted!">
                    {/* Verdict and provenance together: "differs" is only half the
                        story without knowing whether it was graded against canon
                        or just against whatever the reference store happens to hold. */}
                    <TableCell className="pl-4">
                      <div className="flex items-center gap-1.5 whitespace-nowrap">
                        <ScorecardStatusBadge status={row.isDifferent ? "fail" : "pass"} />
                        <OriginBadge origin={row.origin} />
                      </div>
                    </TableCell>
                    {/* Setting over its source table: the table only ever says
                        `configs` or `store_settings`, so a column of its own cost
                        9rem to repeat one of two words on every row. */}
                    <TableCell>
                      <div className="flex flex-col items-start gap-0.5">
                        <KeyChip label={row.setting} />
                        <span className="font-mono text-muted-foreground text-xs">{row.table}</span>
                      </div>
                    </TableCell>
                    {/* Same renderer as the settings list, driven by the type the
                        API reports: switches for booleans, tabular numbers, and
                        JSON behind a preview dialog rather than a truncated blob. */}
                    <TableCell className="max-w-[16rem]">
                      <SettingValueCell value={row.expected} fieldType={row.fieldType} name={row.setting} />
                    </TableCell>
                    <TableCell className="max-w-[16rem]">
                      <SettingValueCell value={row.storeValue} fieldType={row.fieldType} name={row.setting} />
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={4} className="py-16 text-center text-muted-foreground">
                      {t("storeScorecard.noRows")}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * The scorecards dealt into two columns, tallest panel second.
 *
 * A grid tied both panels in a row to the taller one's height, and these differ by a
 * lot — Imports runs to ten checks where Identity has four — so every short panel sat
 * over a hole. CSS `columns` packs them, but the browser decides the split at layout
 * time, which leaves no way to say "first in this column" and so no way to keep the
 * rule off the top of each column. Splitting here does: the two panels that open a
 * column are the ones this array puts first.
 *
 * Imports opens the second column, which is what keeps the two ends roughly level.
 */
const PANEL_COLUMNS: [StoreScorecardPanelName[], StoreScorecardPanelName[]] = [
  STORE_SCORECARD_PANELS.slice(0, Math.ceil(STORE_SCORECARD_PANELS.length / 2)),
  STORE_SCORECARD_PANELS.slice(Math.ceil(STORE_SCORECARD_PANELS.length / 2)),
];

export function StoreScorecardTab({ storeName }: StoreScorecardTabProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const scorecardQueryKey = ["stores", "scorecard", storeName];

  // Counts in-flight fetches across every panel, so the one control reflects
  // the whole tab rather than any single query.
  const isRefreshing = useIsFetching({ queryKey: scorecardQueryKey }) > 0;

  // Read straight off the cache rather than held in state: `useIsFetching` above
  // re-renders on every fetch transition, which is exactly when this changes.
  const checkedStamps = queryClient
    .getQueryCache()
    .findAll({ queryKey: scorecardQueryKey })
    .map((query) => query.state.dataUpdatedAt)
    .filter((stamp) => stamp > 0);
  const lastCheckedAt = checkedStamps.length > 0 ? Math.max(...checkedStamps) : null;

  return (
    <div className="space-y-4 p-6">
      <PanelColumns
        seamToSeparator
        columns={[
          PANEL_COLUMNS[0].map((panel) => <ScorecardPanelSection key={panel} storeName={storeName} panel={panel} />),
          PANEL_COLUMNS[1].map((panel) => <ScorecardPanelSection key={panel} storeName={storeName} panel={panel} />),
        ]}
      />

      <Separator />

      <SettingsDiffSection storeName={storeName} />

      {/* Footer bar mirroring the data-table pages: icon-only refresh on the
          left, with the freshness of what you are looking at beside it. */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9"
            onClick={() => queryClient.invalidateQueries({ queryKey: scorecardQueryKey })}
            disabled={isRefreshing}
            aria-label={t("storeScorecard.refreshAll")}
          >
            <RefreshCw className={cn("h-4 w-4", isRefreshing && "animate-spin")} />
          </Button>
          <p className="text-muted-foreground text-sm">
            {isRefreshing
              ? t("common.loading")
              : lastCheckedAt
                ? t("storeScorecard.lastChecked", {
                    time: formatDistanceToNow(new Date(lastCheckedAt), { addSuffix: true }),
                  })
                : null}
          </p>
        </div>
      </div>
    </div>
  );
}
